import { Component, EventEmitter, Input, Output } from '@angular/core';

/** Estados visuales disponibles para el banner de estado. */
export type BannerState = 'info' | 'warning' | 'success' | 'error' | 'delivering';

/**
 * Banner de estado reutilizable con 4 variantes visuales.
 *
 * El posicionamiento (absolute, fixed, etc.) es responsabilidad
 * del componente padre — este componente sólo maneja la visual.
 *
 * @example
 * <am-status-banner
 *   [state]="bannerState"
 *   [messageKey]="'ssbd.baggageDrop.waitingBag'">
 * </am-status-banner>
 *
 * @example Con ícono personalizado
 * <am-status-banner
 *   [state]="'error'"
 *   [messageKey]="'some.error.key'"
 *   [iconSrc]="'assets/images/my-custom-icon.svg'">
 * </am-status-banner>
 *
 * @example Con boton opcional
 * <am-status-banner
 *   [state]="'warning'"
 *   [messageKey]="'printBagTag.banner.startSearch'"
 *   [buttonText]="'printBagTag.banner.startButton'"
 *   (buttonClick)="onStartSearch()">
 * </am-status-banner>
 */
@Component({
  selector: 'am-status-banner',
  templateUrl: './am-status-banner.component.html',
  styleUrls: ['./am-status-banner.component.scss'],
  standalone: false,
})
export class AmStatusBannerComponent {
  /**
   * Estado visual del banner: determina color, ícono predeterminado y semántica ARIA.
   * @default 'info'
   */
  @Input() state: BannerState = 'info';

  /**
   * Clave de traducción i18n.
   * La pipe `translate` se aplica dentro del componente.
   * @example 'ssbd.baggageDrop.waitingBag'
   */
  @Input() messageKey: string = '';

  /**
   * Ruta al ícono (override opcional).
   * Si no se provee, el componente usa su mapa interno según `state`.
   * @example 'assets/images/my-custom-icon.svg'
   */
  @Input() iconSrc?: string;

  /**
   * Texto del boton opcional.
   */
  @Input() buttonText?: string;

  /** Emite cuando el usuario toca el boton opcional del banner */
  @Output() buttonClick = new EventEmitter<void>();

  /** Mapa interno de íconos predeterminados por estado. */
  readonly iconMap: Record<BannerState, string> = {
    info: 'assets/images/alertBlue.svg',
    warning: 'assets/images/alert/warningAlert.svg',
    success: 'assets/images/CircleonCheck.svg',
    error: 'assets/images/alertSimbol.svg',
    delivering: 'assets/images/alertBlue.svg',
  };

  /** Devuelve la ruta efectiva del ícono: override o mapa interno. */
  get resolvedIcon(): string {
    return this.iconSrc ?? this.iconMap[this.state];
  }
}
